import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import PolybotAIAgent from "./PolybotAIAgent";

const AIAgentPage = () => {
  const { toast } = useToast();
  const { profile } = useAuth();
  const [botName, setBotName] = useState(() => localStorage.getItem('bot_name') || 'AI Agent');
  const [policyContextCache, setPolicyContextCache] = useState<string>("");
  const [lastPolicyRefresh, setLastPolicyRefresh] = useState<Date>(new Date());
  const [chatKey, setChatKey] = useState(0); // Used to reset the chat component

  useEffect(() => {
    if (profile?.company_id) {
      fetchPolicyContext(true);
    }
  }, [profile?.company_id]);

  useEffect(() => {
    // Listen for changes to bot name in localStorage
    const handleStorageChange = () => {
      const newBotName = localStorage.getItem('bot_name') || 'AI Agent';
      if (newBotName !== botName) {
        setBotName(newBotName);
      }
    };

    // Check on mount and periodically
    handleStorageChange();
    const interval = setInterval(handleStorageChange, 1000);

    return () => clearInterval(interval);
  }, [botName]);

  const fetchPolicyContext = async (forceRefresh = false): Promise<string> => {
    if (!profile?.company_id) return "";

    if (!forceRefresh && policyContextCache && (new Date().getTime() - lastPolicyRefresh.getTime()) < 60000) {
      return policyContextCache;
    }

    try {
      const { data: policiesData, error: policiesError } = await supabase
        .from('policies')
        .select(`
          id,
          title,
          description,
          department,
          status,
          version,
          effective_date,
          next_renewal_date,
          created_by,
          assigned_reviewers,
          assigned_approvers,
          created_at,
          updated_at
        `)
        .eq('company_id', profile.company_id)
        .order('created_at', { ascending: false });

      if (policiesError) throw policiesError;

      if (!policiesData || policiesData.length === 0) {
        return "No policies found in the system.";
      }

      const userIds = new Set<string>();
      policiesData.forEach(policy => {
        if (policy.created_by) userIds.add(policy.created_by);
        if (policy.assigned_reviewers) {
          policy.assigned_reviewers.forEach((id: string) => userIds.add(id));
        }
        if (policy.assigned_approvers) {
          policy.assigned_approvers.forEach((id: string) => userIds.add(id));
        }
      });

      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name, designation')
        .in('id', Array.from(userIds));

      if (profilesError) throw profilesError;

      const profileMap = new Map(profilesData?.map(p => [p.id, p]) || []);

      const policyContext = policiesData.map(policy => {
        const creator = profileMap.get(policy.created_by);
        const reviewers = policy.assigned_reviewers?.map((id: string) => profileMap.get(id)?.name).filter(Boolean) || [];
        const approvers = policy.assigned_approvers?.map((id: string) => profileMap.get(id)?.name).filter(Boolean) || [];
        
        const departmentMap: Record<string, string> = {
          'hr': 'Human Resources',
          'it': 'Information Technology',
          'finance': 'Finance',
          'operations': 'Operations',
          'compliance': 'Compliance',
          'security': 'Security',
          'general': 'General'
        };

        return `
- **${policy.title}** (ID: ${policy.id})
  - Department: ${departmentMap[policy.department] || policy.department || 'Not specified'}
  - Status: ${policy.status}
  - Version: ${policy.version}
  - Description: ${policy.description || 'No description available'}
  - Created by: ${creator?.name || 'Unknown'} ${creator?.designation ? `(${creator.designation})` : ''}
  - Effective Date: ${policy.effective_date || 'Not set'}
  - Next Renewal: ${policy.next_renewal_date || 'Not set'}
  - Reviewers: ${reviewers.length > 0 ? reviewers.join(', ') : 'None assigned'}
  - Approvers: ${approvers.length > 0 ? approvers.join(', ') : 'None assigned'}
  - Last Updated: ${new Date(policy.updated_at).toLocaleDateString()}`;
      }).join('\n');

      const contextData = `
## Current Company Policies

The company currently has ${policiesData.length} policies in the system:

${policyContext}

## Policy Status Guide
- **draft**: Policy is being written/edited, not yet submitted for review
- **under_review**: Policy is being reviewed by assigned reviewers
- **pending_approval**: Policy has been reviewed and is awaiting final approval
- **approved**: Policy is approved and active/compliant
- **rejected**: Policy was rejected and needs revision

## Compliance Status
- Approved policies represent areas where the company IS compliant
- Draft/Under Review/Pending policies represent areas where compliance is IN PROGRESS
- Rejected policies represent areas that need IMMEDIATE ATTENTION
`;

      setPolicyContextCache(contextData);
      setLastPolicyRefresh(new Date());
      
      return contextData;
    } catch (error) {
      console.error('Error fetching policy context:', error);
      return "Error loading policy information.";
    }
  };

  const handleNewChat = () => {
    setChatKey(prev => prev + 1);
    toast({
      title: "New Chat Started",
      description: "Started a fresh conversation with your AI assistant.",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-medium text-foreground">{botName}</h1>
            <p className="text-muted-foreground">
              Chat with your AI policy assistant to create, review, and manage company policies
            </p>
          </div>
          <div className="flex items-center gap-2">
            {policyContextCache && (
              <Badge variant="outline" className="border-purple-500 text-purple-700 dark:text-purple-400">
                Policies loaded
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleNewChat}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Chat
            </Button>
          </div>
        </div>
      </div>

      {/* AI Agent Component */}
      <div className="h-[600px]">
        <PolybotAIAgent key={chatKey} />
      </div>
    </div>
  );
};

export default AIAgentPage;
